"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Mic, X } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { springConfig } from "@/lib/animations";

interface VoiceTipModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VoiceTipModal({ isOpen, onClose }: VoiceTipModalProps) {
  const t = useTranslations("voiceTip");

  return (
    <Sheet open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <SheetContent className="h-auto sm:h-auto" disableOutsideClick>
        <DialogPrimitive.Title className="sr-only">{t("title")}</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          {t("description")}
        </DialogPrimitive.Description>

        <div className="flex flex-1 flex-col min-h-0 px-4 pb-4 pt-4 sm:px-6 sm:pt-6">
          <div className="flex justify-end shrink-0">
            <DialogPrimitive.Close className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="flex flex-col">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="flex flex-col gap-6"
            >
              {/* Icon and Title */}
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <Mic className="size-14 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight mb-2">{t("title")}</h2>
                  <p className="text-muted-foreground">{t("description")}</p>
                </div>
              </div>

              {/* Tip Content */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <h3>{t("tipTitle")}</h3>
                <p className="text-muted-foreground">{t("tipDescription")}</p>

                {/* Example */}
                <div className="mt-4 space-y-2">
                  <p>{t("example")}</p>
                  <div className="space-y-2 pl-2">
                    <div className="flex items-center gap-2">
                      <span>"{t("exampleIngredient1")}"</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>⏸️</span>
                      <span className="text-sm italic">{t("pauseLabel")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>"{t("exampleIngredient2")}"</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>⏸️</span>
                      <span className="text-sm italic">{t("pauseLabel")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>"{t("exampleIngredient3")}"</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex flex-col gap-2">
                <Button onClick={onClose} className="w-full">
                  {t("gotIt")}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
